import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Imports réguliers
import LoginPage from './pages/LoginPage';
import ChefZoneLayout from './components/ChefZoneLayout';
import ResponsableLayout from './components/ResponsableLayout';
import SuperviseurEnChefLayout from './components/SuperviseurEnChefLayout';
import AdminLayout from './components/AdminLayout';
import DashboardPage from './pages/DashboardPage';
import ValidationPage from './pages/ValidationPage';
import SupervisorHistoryPage from './pages/SupervisorHistoryPage';
import VisitDetailPage from './pages/VisitDetailPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminGeolocalisationPage from './pages/AdminGeolocalisationPage';
import ControlesTerrainPage from './pages/ControlesTerrainPage';
import ControleTerrainDetailPage from './pages/ControleTerrainDetailPage';
import GeoLayout from './components/GeoLayout';
import HotessesLayout from './components/HotessesLayout';
import SuiviHotessesPage from './pages/SuiviHotessesPage';
import AdminHotessesPage from './pages/AdminHotessesPage';
import AvisConsommateursPage from './pages/AvisConsommateursPage';
import UserManagementPage from './pages/UserManagementPage';
import ProductManagementPage from './pages/ProductManagementPage';
import ValidatedReportsPage from './pages/ValidatedReportsPage';
import ClientManagementPage from './pages/ClientManagementPage';
import ProspectsClientsPage from './pages/ProspectsClientsPage';
import CommercialManagementPage from './pages/CommercialManagementPage';
import ZoneManagementPage from './pages/ZoneManagementPage';
import ExportExcelPage from './pages/ExportExcelPage';
import RejectedReportsPage from './pages/RejectedReportsPage';
import HomeRedirector from './pages/HomeRedirector';
import './App.css';

// Imports lazy pour le Superviseur en Chef
const SuperviseurEnChefDashboardPage = lazy(() => import('./pages/SuperviseurEnChefDashboardPage'));
const SuperviseurEnChefValidationPage = lazy(() => import('./pages/SuperviseurEnChefValidationPage'));
const SuperviseurEnChefValidatedReportsPage = lazy(() => import('./pages/SuperviseurEnChefValidatedReportsPage'));
const SuperviseurEnChefVisitDetailPage = lazy(() => import('./pages/SuperviseurEnChefVisitDetailPage'));
const SuperviseurEnChefRejectedReportsPage = lazy(() => import('./pages/SuperviseurEnChefRejectedReportsPage'));
const SuperviseurEnChefExportPage = lazy(() => import('./pages/SuperviseurEnChefExportPage'));

// Le garde du corps ne change pas
function PrivateRoute({ children }) {
  const token = localStorage.getItem('authToken');
  return token ? children : <Navigate to="/login" />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('authToken'));
  const navigate = useNavigate();

  const handleLoginSuccess = (loginData) => {
    localStorage.setItem('authToken', loginData.access_token);
    localStorage.setItem('userRole', loginData.user_role); 
    setIsAuthenticated(true);
    navigate('/');
  };
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    setIsAuthenticated(false);
    navigate('/login');
  };

  return (
    <Routes>
      <Route 
        path="/login" 
        element={!isAuthenticated ? <LoginPage onLoginSuccess={handleLoginSuccess} /> : <Navigate to="/" />}
      />

      {/* --- Route Portail de Redirection --- */}
      <Route path="/" element={<PrivateRoute><HomeRedirector /></PrivateRoute>} />

      {/* --- GROUPE DES ROUTES DU CHEF DE ZONE --- */}
      <Route path="/chef-zone" element={<PrivateRoute><ChefZoneLayout onLogout={handleLogout} /></PrivateRoute>}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="validations" element={<ValidationPage />} />
        <Route path="rejetes" element={<RejectedReportsPage />} />
        <Route path="historique" element={<SupervisorHistoryPage />} />
        <Route path="visite/:visiteId" element={<VisitDetailPage />} />
        <Route path="export" element={<ExportExcelPage />} />
        <Route path="controles-terrain" element={<ControlesTerrainPage />} />
        <Route path="controle-terrain/:controleId" element={<ControleTerrainDetailPage />} />
      </Route>

      {/* --- GROUPE DES ROUTES DU RESPONSABLE --- */}
      <Route path="/responsable" element={<PrivateRoute><ResponsableLayout onLogout={handleLogout} /></PrivateRoute>}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="validations" element={<ValidationPage />} />
        <Route path="rejetes" element={<RejectedReportsPage />} />
        <Route path="historique" element={<SupervisorHistoryPage />} />
        <Route path="visite/:visiteId" element={<VisitDetailPage />} />
        <Route path="chefs-zone" element={<UserManagementPage />} />
        <Route path="controles-terrain" element={<ControlesTerrainPage />} />
        <Route path="controle-terrain/:controleId" element={<ControleTerrainDetailPage />} />
      </Route>



     {/* --- GROUPE DES ROUTES DU SUPERVISEUR EN CHEF --- */}
        <Route path="/superviseur-en-chef" element={<PrivateRoute><Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefLayout onLogout={handleLogout} /></Suspense></PrivateRoute>}>
          <Route index element={<Navigate to="dashboard" />} />
          <Route path="dashboard" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefDashboardPage /></Suspense>} />
          <Route path="validations" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefValidationPage /></Suspense>} />
          <Route path="validees" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefValidatedReportsPage /></Suspense>} />
          <Route path="rejetes" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefRejectedReportsPage /></Suspense>} />
          <Route path="visite/:visiteId" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefVisitDetailPage /></Suspense>} />
          <Route path="export" element={<Suspense fallback={<div>Chargement...</div>}><SuperviseurEnChefExportPage /></Suspense>} />
        </Route>


	  
      {/* --- GROUPE DES ROUTES DE L'ADMINISTRATEUR --- */}
      <Route path="/admin" element={<PrivateRoute><AdminLayout onLogout={handleLogout} /></PrivateRoute>}>
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="products" element={<ProductManagementPage />} />
        <Route path="clients" element={<ClientManagementPage />} />
        <Route path="prospects" element={<ProspectsClientsPage />} />
        <Route path="commerciaux" element={<CommercialManagementPage />} />
        <Route path="zones" element={<ZoneManagementPage />} />
        <Route path="geolocalisation" element={<AdminGeolocalisationPage />} />
        <Route path="reports" element={<ValidatedReportsPage />} />
        <Route path="controles-terrain" element={<ControlesTerrainPage />} />
        <Route path="controle-terrain/:controleId" element={<ControleTerrainDetailPage />} />
        <Route path="hotesses" element={<AdminHotessesPage />} />
        <Route path="presence-hotesses" element={<SuiviHotessesPage />} />
        <Route path="avis-consommateurs" element={<AvisConsommateursPage />} />
      </Route>

      {/* --- GROUPE DES ROUTES DU RESPONSABLE DES HOTESSES --- */}
      <Route path="/responsable-hotesses" element={<PrivateRoute><HotessesLayout onLogout={handleLogout} /></PrivateRoute>}>
        <Route index element={<Navigate to="presence" />} />
        <Route path="presence" element={<SuiviHotessesPage />} />
        <Route path="avis-consommateurs" element={<AvisConsommateursPage />} />
        <Route path="controles-terrain" element={<ControlesTerrainPage />} />
        <Route path="controle-terrain/:controleId" element={<ControleTerrainDetailPage />} />
      </Route>

      {/* --- GROUPE DES ROUTES GEO OBSERVATEUR --- */}
      <Route path="/geo" element={<PrivateRoute><GeoLayout onLogout={handleLogout} /></PrivateRoute>}>
        <Route index element={<Navigate to="geolocalisation" />} />
        <Route path="geolocalisation" element={<AdminGeolocalisationPage />} />
      </Route>

      {/* La route "attrape-tout" finale */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
